import { useRef, useState, type ChangeEvent } from 'react';

import BpkInput from '@skyscanner-internal/backpack-web/bpk-component-input';
import BpkPagination from '@skyscanner-internal/backpack-web/bpk-component-pagination';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import { useTranslations } from '@web-platform/shared-travel-tools-utils-frontend';
import { useDestinations } from '@web-platform/travel-tools-flight-price-map-data-access-frontend';
import {
  FlightCardList,
  PriceDisclaimerBanner,
} from '@web-platform/travel-tools-flight-price-map-ui';
import { filterForDestinations } from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import STYLES from './ListPanel.module.scss';

const cls = cssModules(STYLES);

const PAGE_SIZE = 15;

type Props = {
  onCardHover?: (id: string) => void;
  onCardHoverLeave?: () => void;
};

export const ListPanel = ({ onCardHover, onCardHoverLeave }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslations();
  const destinations = useDestinations();

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setCurrentPage(0);
  };

  const filteredCards = filterForDestinations(destinations, searchQuery);
  const pageCount = Math.ceil(filteredCards.length / PAGE_SIZE);
  const visualCards = filteredCards.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  return (
    <div className={cls('listPanel')}>
      <BpkInput
        id="list-panel-search"
        name="list-panel-search"
        className={cls('searchInput')}
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={translate('travel_confidence_map_search_placeholder')}
        clearButtonMode="whileEditing"
        clearButtonLabel={translate('travel_confidence_map_search_clear')}
        onClear={handleSearchClear}
      />
      <div ref={listRef} className={cls('list')}>
        <PriceDisclaimerBanner />
        <FlightCardList
          cards={visualCards}
          onCardHover={onCardHover}
          onCardHoverLeave={onCardHoverLeave}
        />
        {pageCount > 1 && (
          <BpkPagination
            pageCount={pageCount}
            selectedPageIndex={currentPage}
            onPageChange={handlePageChange}
            previousLabel={translate('travel_confidence_map_pagination_next')}
            nextLabel={translate('travel_confidence_map_pagination_previous')}
            paginationLabel={translate('travel_confidence_map_pagination_label')}
            pageLabel={(page: number) =>
              translate('travel_confidence_map_pagination_page', { number: page, total: pageCount })
            }
          />
        )}
      </div>
    </div>
  );
};
